import { ReactNode, useMemo } from "react";
import { SafeReportResponse } from "../Conversion";
import { useCalculatedPremium } from "../data";
import { readableLimitOfLiabilityFromLayer } from "../layers/util";
import {
  lossRunInfoToLossRunYears,
  lossRunStatsForWindow,
} from "../loss-run/util";
import { formatAbbreviatedMoney } from "../Money";
import styles from "./MobileTabSummaries.module.css";

type MobileSummaryProps = { specialtyPropertyInfo: SafeReportResponse };

const Stat = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className={styles.stat}>
    <div className={styles.statValue}>{value}</div>
    <div className={styles.statLabel}>{label}</div>
  </div>
);

export const SovMobileSummary = ({
  specialtyPropertyInfo,
}: MobileSummaryProps) => {
  const pm = specialtyPropertyInfo.precomputed_metadata;
  const states = pm?.unique_location_states ?? [];
  return (
    <div className={styles.summary}>
      <div className={styles.statRow}>
        <Stat
          label="TIV"
          value={
            pm?.total_tiv != null ? formatAbbreviatedMoney(pm.total_tiv) : "—"
          }
        />
        <Stat label="Locations" value={pm?.num_locations ?? "—"} />
        <Stat label="States" value={states.length || "—"} />
      </div>
      {states.length > 0 && (
        <div className={styles.chips}>
          {states.map((state) => (
            <span key={state} className={styles.chip}>
              {state}
            </span>
          ))}
        </div>
      )}
      <div className={styles.note}>
        Full location schedule is available on desktop.
      </div>
    </div>
  );
};

export const LayersMobileSummary = ({
  specialtyPropertyInfo,
}: MobileSummaryProps) => {
  const layers = (specialtyPropertyInfo.report_json.layer_info ?? []).filter(
    (layer) => layer.is_enabled !== false
  );
  const { data: premiumData } = useCalculatedPremium(
    specialtyPropertyInfo.report_id,
    true
  );
  const premiumByLayer = useMemo(
    () =>
      new Map((premiumData?.layers ?? []).map((l) => [l.layer_id, l] as const)),
    [premiumData]
  );

  if (layers.length === 0) {
    return (
      <div className={styles.summary}>
        <div className={styles.note}>No layers on this submission.</div>
      </div>
    );
  }

  return (
    <div className={styles.summary}>
      <div className={styles.list}>
        {layers.map((layer, index) => {
          const breakdown = layer.layer_id
            ? premiumByLayer.get(layer.layer_id)
            : undefined;
          const aal = breakdown
            ? Object.values(breakdown.peril_types).reduce(
                (sum, pt) => sum + (pt.resolved.total_aal ?? 0),
                0
              )
            : undefined;
          const gross = breakdown?.totals["resolved"]?.layer_total;
          const share = breakdown?.totals["resolved"]?.share_total;
          return (
            <div key={layer.layer_id ?? index} className={styles.layerRow}>
              <div className={styles.layerName}>
                {readableLimitOfLiabilityFromLayer(layer)}
              </div>
              <div className={styles.layerStats}>
                {layer.target != null && (
                  <div className={styles.miniStat}>
                    <span className={styles.miniStatValue}>
                      {formatAbbreviatedMoney(layer.target)}
                    </span>
                    <span className={styles.miniStatLabel}>Target</span>
                  </div>
                )}
                {aal != null && aal > 0 && (
                  <div className={styles.miniStat}>
                    <span className={`${styles.miniStatValue} ${styles.aal}`}>
                      {formatAbbreviatedMoney(aal)}
                    </span>
                    <span className={styles.miniStatLabel}>AAL</span>
                  </div>
                )}
                {gross != null && (
                  <div className={styles.miniStat}>
                    <span className={styles.miniStatValue}>
                      {formatAbbreviatedMoney(gross)}
                    </span>
                    <span className={styles.miniStatLabel}>Gross</span>
                  </div>
                )}
                {share != null && (
                  <div className={styles.miniStat}>
                    <span className={`${styles.miniStatValue} ${styles.share}`}>
                      {formatAbbreviatedMoney(share)}
                    </span>
                    <span className={styles.miniStatLabel}>Share</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className={styles.note}>
        Full layer pricing and editing is available on desktop.
      </div>
    </div>
  );
};

export const LossRunMobileSummary = ({
  specialtyPropertyInfo,
}: MobileSummaryProps) => {
  const lossRunInfo = specialtyPropertyInfo.report_json.loss_run;
  const stats = lossRunStatsForWindow(
    lossRunInfoToLossRunYears(lossRunInfo),
    5
  );
  if (stats.yearsCovered === 0) {
    return (
      <div className={styles.summary}>
        <div className={styles.note}>No loss history on this submission.</div>
      </div>
    );
  }
  return (
    <div className={styles.summary}>
      <div className={styles.statRow}>
        <Stat
          label={`Ground up · ${stats.windowYears}yr avg`}
          value={
            stats.avgGroundUpLoss != null
              ? `${formatAbbreviatedMoney(stats.avgGroundUpLoss)}/y`
              : "—"
          }
        />
        <Stat
          label={`Net incurred · ${stats.windowYears}yr avg`}
          value={
            stats.avgNetIncurred != null
              ? `${formatAbbreviatedMoney(stats.avgNetIncurred)}/y`
              : "—"
          }
        />
      </div>
      <div className={styles.statRow}>
        <Stat
          label="Ground up total"
          value={
            stats.totalGroundUpLoss != null
              ? formatAbbreviatedMoney(stats.totalGroundUpLoss)
              : "—"
          }
        />
        <Stat
          label="Net incurred total"
          value={
            stats.totalNetIncurred != null
              ? formatAbbreviatedMoney(stats.totalNetIncurred)
              : "—"
          }
        />
        <Stat label="Years" value={stats.yearsCovered} />
      </div>
      <div className={styles.note}>Full loss run is available on desktop.</div>
    </div>
  );
};
